import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  XMarkIcon, 
  UserIcon, 
  ClockIcon, 
  CalendarDaysIcon,
  PlusIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import api from '@/services/api'
import toast from 'react-hot-toast'

interface Employee {
  id: number
  employeeNumber: string
  firstName: string
  lastName: string
  department: string
  position: string
}

interface LeaveType {
  id: number
  name: string
  code: string
  isPaid: boolean
}

interface LeaveBalance {
  id: number
  employeeId: number
  leaveTypeId: number
  year: number
  allocatedDays: number
  usedDays: number
  pendingDays: number
  carriedForwardDays: number
  availableDays: number
  leaveType: LeaveType
}

interface LeaveBalancesResponse {
  balances: LeaveBalance[]
  year: number
}

interface EmployeesResponse {
  employees: Employee[]
}

interface EmployeeLeaveBalancesModalProps {
  onClose: () => void
}

const EmployeeLeaveBalancesModal: React.FC<EmployeeLeaveBalancesModalProps> = ({ onClose }) => {
  const { user } = useAuth()
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [showEmployeeSelect] = useState(user?.role !== 'employee')

  const queryClient = useQueryClient()

  // Get employees list (for HR/managers)
  const { data: employeesData } = useQuery<EmployeesResponse>({
    queryKey: ['employees'],
    queryFn: async () => {
      const response = await api.get('/employees?limit=100')
      return response.data.data
    },
    enabled: user?.role !== 'employee'
  })

  // Get current user's employee record (for employees)
  const { data: currentEmployee } = useQuery<Employee>({
    queryKey: ['currentEmployee'],
    queryFn: async () => {
      const response = await api.get('/auth/profile')
      return response.data.data.user.employee
    },
    enabled: user?.role === 'employee'
  })

  // Get leave balances for selected employee
  const employeeId = user?.role === 'employee' 
    ? currentEmployee?.id 
    : selectedEmployee?.id

  const { data: balancesData, isLoading: balancesLoading } = useQuery<LeaveBalancesResponse>({
    queryKey: ['leaveBalances', employeeId, selectedYear],
    queryFn: async () => {
      const response = await api.get(`/leaves/balances/employee/${employeeId}?year=${selectedYear}`)
      return response.data.data
    },
    enabled: !!employeeId
  })

  // Initialize balances mutation
  const initializeBalancesMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/leaves/balances/employee/${employeeId}/initialize?year=${selectedYear}`)
      return response.data
    },
    onSuccess: () => {
      toast.success('Leave balances initialized successfully')
      queryClient.invalidateQueries({ queryKey: ['leaveBalances', employeeId, selectedYear] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to initialize leave balances')
    }
  })

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)

  const selectedEmployeeDisplay = user?.role === 'employee' 
    ? currentEmployee 
    : selectedEmployee

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-5xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between pb-3 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            Leave Balances
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="mt-4">
          {/* Employee and Year Selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {showEmployeeSelect && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Employee
                </label>
                <select
                  value={selectedEmployee?.id || ''}
                  onChange={(e) => {
                    const employee = employeesData?.employees.find(emp => emp.id === parseInt(e.target.value))
                    setSelectedEmployee(employee || null)
                  }}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">Select an employee...</option>
                  {employeesData?.employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.firstName} {employee.lastName} ({employee.employeeNumber})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Year
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                {years.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {selectedEmployeeDisplay && !balancesData?.balances?.length && (
              <div className="flex items-end">
                <button
                  onClick={() => initializeBalancesMutation.mutate()}
                  disabled={initializeBalancesMutation.isPending}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                  {initializeBalancesMutation.isPending ? 'Initializing...' : 'Initialize Balances'}
                </button>
              </div>
            )}
          </div>

          {/* Selected Employee Info */}
          {selectedEmployeeDisplay && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <UserIcon className="h-8 w-8 text-gray-400 mr-3" />
                <div>
                  <h4 className="text-lg font-medium text-gray-900">
                    {selectedEmployeeDisplay.firstName} {selectedEmployeeDisplay.lastName}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {selectedEmployeeDisplay.employeeNumber} • {selectedEmployeeDisplay.department} • {selectedEmployeeDisplay.position}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Leave Balances */}
          {!selectedEmployeeDisplay ? (
            <div className="text-center py-12">
              <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Select an employee</h3>
              <p className="mt-1 text-sm text-gray-500">
                Choose an employee to view their leave balances.
              </p>
            </div>
          ) : balancesLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-sm text-gray-500">Loading leave balances...</p>
            </div>
          ) : !balancesData?.balances?.length ? (
            <div className="text-center py-12">
              <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No leave balances found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Initialize leave balances for {selectedYear} to get started.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => initializeBalancesMutation.mutate()}
                  disabled={initializeBalancesMutation.isPending}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                  {initializeBalancesMutation.isPending ? 'Initializing...' : 'Initialize Balances'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-medium text-gray-900">
                  Leave Balances for {selectedYear}
                </h4>
              </div>

              <div className="grid gap-4">
                {balancesData.balances.map((balance) => (
                  <div key={balance.id} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <CalendarDaysIcon className="h-8 w-8 text-primary-600" />
                        </div>
                        <div>
                          <h5 className="text-lg font-medium text-gray-900">
                            {balance.leaveType.name}
                          </h5>
                          <div className="flex items-center space-x-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {balance.leaveType.code}
                            </span>
                            {balance.leaveType.isPaid && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Paid
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900">
                          {balance.availableDays}
                        </div>
                        <div className="text-sm text-gray-500">Days Available</div>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-lg font-semibold text-blue-600">
                          {balance.allocatedDays}
                        </div>
                        <div className="text-xs text-gray-500">Allocated</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-green-600">
                          {balance.carriedForwardDays}
                        </div>
                        <div className="text-xs text-gray-500">Carried Forward</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-red-600">
                          {balance.usedDays}
                        </div>
                        <div className="text-xs text-gray-500">Used</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-yellow-600">
                          {balance.pendingDays}
                        </div>
                        <div className="text-xs text-gray-500">Pending</div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Leave Usage</span>
                        <span>
                          {balance.usedDays + balance.pendingDays} / {balance.allocatedDays + balance.carriedForwardDays} days
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full"
                          style={{
                            width: `${Math.min(
                              ((balance.usedDays + balance.pendingDays) / (balance.allocatedDays + balance.carriedForwardDays)) * 100,
                              100
                            )}%`
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default EmployeeLeaveBalancesModal